import React from 'react';
import { motion } from 'framer-motion';
import { useHistory, useParams } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Info, Clock } from 'lucide-react';
import UserLayout from './user/UserLayout';
import { getCatalogueBySlug, CATALOGUES_PATH } from '../data/stauffCatalogues';

const StauffCatalogueDetail = () => {
  const history = useHistory();
  const { slug } = useParams();
  const catalogue = getCatalogueBySlug(slug);
  const title = catalogue ? catalogue.title : 'Catalogue Not Found';

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
            <nav className="text-sm text-gray-600 mt-1">
              <button
                onClick={() => history.push('/user/dashboard')}
                className="hover:text-brand transition-colors duration-200"
              >
                Dashboard
              </button>
              <span className="mx-2">{'>'}</span>
              <button
                onClick={() => history.push('/user/product-documents')}
                className="hover:text-brand transition-colors duration-200"
              >
                Product Documents
              </button>
              <span className="mx-2">{'>'}</span>
              <button
                onClick={() => history.push(CATALOGUES_PATH)}
                className="hover:text-brand transition-colors duration-200"
              >
                STAUFF Catalogues
              </button>
              <span className="mx-2">{'>'}</span>
              <span>{title}</span>
            </nav>
          </div>
          <div className="mt-4 sm:mt-0">
            <button
              onClick={() => history.push(CATALOGUES_PATH)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Catalogues
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {!catalogue ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">Catalogue not found</h3>
                <p className="text-gray-500">This catalogue doesn't exist. Choose one from the STAUFF catalogue list.</p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <img
                  src={catalogue.imageUrl}
                  alt={catalogue.title}
                  className="w-full h-64 lg:h-full object-cover"
                />
                <div className="p-6 lg:p-8 flex flex-col justify-center">
                  <div className="inline-flex items-center self-start px-3 py-1 mb-4 bg-brand bg-opacity-10 rounded-full border border-brand border-opacity-20">
                    <FileText className="w-4 h-4 text-brand mr-2" />
                    <span className="text-sm font-medium text-brand-dark">STAUFF Product Catalogue</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{catalogue.title}</h2>
                  <p className="text-gray-600 mb-6">{catalogue.description}</p>

                  {catalogue.pdfUrl ? (
                    <>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Format:</span>
                          <span className="font-medium text-gray-900">PDF</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Size:</span>
                          <span className="font-medium text-gray-900">{catalogue.fileSize}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Language:</span>
                          <span className="font-medium text-gray-900">English</span>
                        </div>
                      </div>

                      <a
                        href={catalogue.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 bg-brand text-brand-dark rounded-lg font-semibold hover:bg-brand-hover hover:shadow-lg transition-all duration-200"
                      >
                        <Download className="w-5 h-5 mr-2" />
                        View / Download PDF
                      </a>

                      <p className="flex items-start text-xs text-gray-500 mt-4">
                        <Info className="w-4 h-4 mr-1 flex-shrink-0" />
                        This is a large file and may take a while to open on slower connections.
                      </p>
                    </>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start">
                      <Clock className="w-5 h-5 text-gray-500 mr-3 flex-shrink-0" />
                      <p className="text-sm text-gray-600">
                        This catalogue isn't available for download yet. Contact our sales team for a copy.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </UserLayout>
  );
};

export default StauffCatalogueDetail;
